import React from 'react'
import {
  WrappedFieldProps,
  Field,
  getFormValues,
  SubmissionError,
} from 'redux-form'
import get from 'lodash/get'
import { ElementsConsumer, CardElement } from '@stripe/react-stripe-js'
import { connect } from 'react-redux'
import axios from 'axios'

import { useUserCreditCardsQuery } from 'hooks/useQueries'
import NewCreditCardFields from 'pages/user/PaymentMethods/NewCreditCardFields'
import Spinner from 'components/display/Spinner'
import { InputGroup, SelectField } from 'components/form'
import ChevronDown from 'components/icons/ChevronDown2'
import { HeadingMd, ParagraphSm } from 'components/display/Typography'

import { Stripe, StripeElements } from '@stripe/stripe-js'

type WithUserCreditCardQueryProps = {
  onCompleted: Parameters<typeof useUserCreditCardsQuery>[0]['onCompleted']
}

const UserCreditCardsQuery: React.FC<WithUserCreditCardQueryProps> = ({
  children,
  onCompleted,
}) => {
  const { loading, data } = useUserCreditCardsQuery({ onCompleted })

  if (typeof children !== 'function') {
    throw new Error(
      'children passed to UserCreditCardsQuery must be a function'
    )
  }

  return children({ loading, data })
}

export type SelectPaymentMethodFormValues = {
  payment_method: {
    credit_card_id: string
    new_card_name: string
  }
}

interface ReduxState {
  [formName: string]: SelectPaymentMethodFormValues
}

type BaseProps = {
  error?: SubmissionError
  cardFieldsError?: string
  rounded?: boolean
  headingType?: 'h2' | 'h3' | 'h4'
  headingWeight?: '300' | '400' | '500'
}

export type SelectPaymentMethodProps = WrappedFieldProps &
  BaseProps & {
    elements: StripeElements
    stripe: Stripe
    reduxState: ReduxState
  }

class SelectPaymentMethod extends React.Component<SelectPaymentMethodProps> {
  getCreditCardId = async (): Promise<string> => {
    const { input, meta, elements, stripe, reduxState } = this.props
    const cardElement = elements.getElement(CardElement)
    /**
     * We need to return an existing credit card id if selected
     */
    if (input.value !== 'new') {
      return input.value
    }

    const {
      payment_method: { new_card_name: newCardName },
    } = getFormValues(meta.form)(reduxState) as SelectPaymentMethodFormValues

    let stripeToken: string

    try {
      const { token } = await stripe.createToken(cardElement, {
        name: newCardName,
      })
      stripeToken = token?.id
    } catch (error) {
      throw new SubmissionError({
        _error: 'Could not add the credit card. Please contact support.',
      })
    }

    if (stripeToken == null) {
      throw new SubmissionError({
        _error: 'Could not add the credit card. Please contact support.',
      })
    }

    // create new credit card and get its ID
    return axios
      .post('/user/credit_cards', {
        stripe_token: stripeToken,
      })
      .then(({ data }) => data.id)
      .catch((error) => {
        if (error.response) {
          const { data } = error.response
          const { error: errorMessage } = data
          throw new SubmissionError({
            // redux-form api -- `_error` applies to the whole form, rather than specific fields
            // we pass it down to NewCreditCardFields, which then knows to show inline feedback
            _error: errorMessage,
          })
        }
      })
  }

  handleCompleted = (data: GQL_UserCreditCardsQuery) => {
    const { input } = this.props
    const currentUser = get(data, 'currentUser')
    const preferred = currentUser.creditCards
      .filter((c) => !c.expired)
      .sort((a, b) => Number(b.default) - Number(a.default))
    if (preferred.length) return input.onChange(preferred[0].id)
    return input.onChange('new')
  }

  render() {
    const {
      input,
      meta,
      error: formError,
      rounded,
      headingType = 'h2',
      headingWeight = '500',
    } = this.props

    const error = meta.submitFailed ? formError : undefined

    return (
      <UserCreditCardsQuery onCompleted={this.handleCompleted}>
        {({
          loading,
          data,
        }: Pick<
          ReturnType<typeof useUserCreditCardsQuery>,
          'loading' | 'data'
        >) => {
          const currentUser = get(data, 'currentUser')
          if (loading) {
            return (
              <InputGroup>
                <Spinner />
              </InputGroup>
            )
          }

          return (
            <div>
              <HeadingMd as={headingType} weight={headingWeight}>
                Select payment method
              </HeadingMd>

              <InputGroup style={{ marginBottom: '16px' }}>
                <SelectField className="select-field" rounded={rounded}>
                  &nbsp;
                  <select {...input} aria-label="Payment method">
                    {currentUser.creditCards.map(
                      ({ id, brand, last4, expired }) => (
                        <option key={id} value={id} disabled={expired}>
                          {brand} ending in {last4}
                        </option>
                      )
                    )}
                    <option value="new">Add a credit card</option>
                  </select>
                  <span>
                    <ChevronDown style={{ width: '20px', height: 'auto' }} />
                  </span>
                </SelectField>
              </InputGroup>

              {input.value === 'new' && (
                <InputGroup data-testid="add-a-new-card-wrapper" noMargin>
                  <ParagraphSm noMargin weight="400">
                    Add a new card
                  </ParagraphSm>
                  <NewCreditCardFields
                    submitFailed={meta.submitFailed}
                    error={error}
                  />
                </InputGroup>
              )}
            </div>
          )
        }}
      </UserCreditCardsQuery>
    )
  }
}

const SelectPaymentMethodWithStripe = React.forwardRef<any, BaseProps>(
  (props, ref) => (
    <ElementsConsumer>
      {({ elements, stripe }) => (
        <Field
          ref={ref}
          name="payment_method[credit_card_id]"
          component={SelectPaymentMethod}
          stripe={stripe}
          elements={elements}
          forwardRef
          {...props}
        />
      )}
    </ElementsConsumer>
  )
)

export default connect(
  ({ form }: ReduxState) => ({ reduxState: { form } }),
  null,
  null,
  {
    forwardRef: true,
  }
)(SelectPaymentMethodWithStripe)
